import {
  useId,
  type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Icono, type NombreIcono } from './Icono';

interface PropsComunes {
  etiqueta?: ReactNode;
  ayuda?: ReactNode;
  /** Mensaje ya listo para mostrar. Con error el campo se pinta y se anuncia. */
  error?: string | null;
}

export function Etiqueta({ htmlFor, requerido, children }: {
  htmlFor: string;
  requerido?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="campo__etiqueta" htmlFor={htmlFor}>
      {children}
      {requerido && <span className="campo__requerido" aria-hidden="true">*</span>}
    </label>
  );
}

export function ErrorDeCampo({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <div id={id} className="campo__nota campo__nota--error">
      <Icono nombre="alert-circle" tamano={13} />
      {children}
    </div>
  );
}

function Nota({ id, error, ayuda }: { id: string } & Pick<PropsComunes, 'error' | 'ayuda'>) {
  if (error) return <ErrorDeCampo id={id}>{error}</ErrorDeCampo>;
  if (ayuda) return <div id={id} className="campo__nota">{ayuda}</div>;
  return null;
}

// El id se genera si la pantalla no manda uno, para que la etiqueta y el mensaje
// queden siempre atados al control y el lector de pantalla los lea juntos.
function useIds(propio: string | undefined, conNota: boolean) {
  const generado = useId();
  const id = propio ?? generado;
  return { id, idNota: `${id}-nota`, describe: conNota ? `${id}-nota` : undefined };
}

type PropsEntrada = PropsComunes & Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  icono?: NombreIcono;
  sufijo?: ReactNode;
  /** Botón o adorno al final de la caja (por ejemplo, el ojo de la contraseña). */
  extremo?: ReactNode;
};

export function Entrada({
  etiqueta, ayuda, error, icono, sufijo, extremo, id: propio, required, disabled, className, ...resto
}: PropsEntrada) {
  const { id, idNota, describe } = useIds(propio, Boolean(error || ayuda));
  const caja = ['campo__caja', error && 'campo__caja--error', disabled && 'campo__caja--apagada']
    .filter(Boolean).join(' ');

  return (
    <div className={className ? `campo ${className}` : 'campo'}>
      {etiqueta && <Etiqueta htmlFor={id} requerido={required}>{etiqueta}</Etiqueta>}
      <div className={caja}>
        {icono && <Icono nombre={icono} tamano={18} className="campo__icono" />}
        <input
          id={id}
          className="campo__control"
          required={required}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describe}
          {...resto}
        />
        {sufijo && <span className="campo__sufijo">{sufijo}</span>}
        {extremo}
      </div>
      <Nota id={idNota} error={error} ayuda={ayuda} />
    </div>
  );
}

export interface OpcionSelector {
  valor: string;
  texto: string;
}

type PropsSelector = PropsComunes & SelectHTMLAttributes<HTMLSelectElement> & {
  opciones: readonly (OpcionSelector | string)[];
  /** Primera opción vacía, por ejemplo "Elegí uno". */
  marcador?: string;
};

export function Selector({
  etiqueta, ayuda, error, opciones, marcador, id: propio, required, className, value, ...resto
}: PropsSelector) {
  const { id, idNota, describe } = useIds(propio, Boolean(error || ayuda));
  const vacio = value === '' || value === undefined;
  const caja = ['campo__caja', 'campo__caja--selector', error && 'campo__caja--error']
    .filter(Boolean).join(' ');

  return (
    <div className={className ? `campo ${className}` : 'campo'}>
      {etiqueta && <Etiqueta htmlFor={id} requerido={required}>{etiqueta}</Etiqueta>}
      <div className={caja}>
        <select
          id={id}
          className={vacio ? 'campo__control campo__control--vacio' : 'campo__control'}
          value={value}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describe}
          {...resto}
        >
          {marcador !== undefined && <option value="">{marcador}</option>}
          {opciones.map((o) => {
            const { valor, texto } = typeof o === 'string' ? { valor: o, texto: o } : o;
            return <option key={valor} value={valor}>{texto}</option>;
          })}
        </select>
        <Icono nombre="chevron-down" tamano={16} className="campo__flecha" />
      </div>
      <Nota id={idNota} error={error} ayuda={ayuda} />
    </div>
  );
}

export function Contador({ valor, maximo }: { valor: string; maximo: number }) {
  const cerca = valor.length > maximo * 0.85;
  return (
    <div className={cerca ? 'contador contador--cerca' : 'contador'} aria-live="polite">
      {valor.length}/{maximo}
    </div>
  );
}

type PropsAreaTexto = PropsComunes & TextareaHTMLAttributes<HTMLTextAreaElement>;

export function AreaTexto({
  etiqueta, ayuda, error, id: propio, required, rows = 3, maxLength, value, className, ...resto
}: PropsAreaTexto) {
  const { id, idNota, describe } = useIds(propio, Boolean(error || ayuda));
  return (
    <div className={className ? `campo ${className}` : 'campo'}>
      {etiqueta && <Etiqueta htmlFor={id} requerido={required}>{etiqueta}</Etiqueta>}
      <textarea
        id={id}
        className={error ? 'campo__area campo__area--error' : 'campo__area'}
        rows={rows}
        value={value}
        maxLength={maxLength}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describe}
        {...resto}
      />
      {maxLength !== undefined && typeof value === 'string' && (
        <Contador valor={value} maximo={maxLength} />
      )}
      <Nota id={idNota} error={error} ayuda={ayuda} />
    </div>
  );
}

type PropsCasilla = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { etiqueta?: ReactNode };

export function Casilla({ etiqueta, className, ...resto }: PropsCasilla) {
  return (
    <label className={className ? `casilla ${className}` : 'casilla'}>
      <input type="checkbox" className="solo-lectores" {...resto} />
      <span className="casilla__marca" aria-hidden="true">
        <Icono nombre="check" tamano={12} grosor={3} />
      </span>
      {etiqueta && <span className="casilla__texto">{etiqueta}</span>}
    </label>
  );
}

export function Interruptor({ etiqueta, className, ...resto }: PropsCasilla) {
  return (
    <label className={className ? `interruptor ${className}` : 'interruptor'}>
      <input type="checkbox" role="switch" className="solo-lectores" {...resto} />
      <span className="interruptor__riel" aria-hidden="true" />
      {etiqueta && <span className="casilla__texto">{etiqueta}</span>}
    </label>
  );
}

type PropsOpcionTarjeta = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  titulo: ReactNode;
  detalle?: ReactNode;
  /** Logo o adorno a la derecha. */
  extra?: ReactNode;
};

export function OpcionTarjeta({ titulo, detalle, extra, className, ...resto }: PropsOpcionTarjeta) {
  return (
    <label className={className ? `opcion ${className}` : 'opcion'}>
      <input type="radio" className="solo-lectores" {...resto} />
      <span className="opcion__punto" aria-hidden="true" />
      <span className="opcion__textos">
        <span className="opcion__titulo">{titulo}</span>
        {detalle && <span className="opcion__detalle">{detalle}</span>}
      </span>
      {extra}
    </label>
  );
}
